import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';

/**
 * An SEO service to dynamically add canonical links to the DOM.
 */
@Injectable()
export class CanonicalLinkService {

  private isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.isServer = isPlatformServer(platformId);
  }

  /**
   * Sets the canonical tag to be the current url.
   * @param stripQueryParams should query parameters be removed from the url
   */
  public setCanonicalTag(stripQueryParams: boolean = true): void {
    if (this.isServer) return;

    const canonicalUrl = stripQueryParams ? this.document.URL.split('?')[0] : this.document.URL;

    this.updateTag({
        rel: 'canonical',
        href: canonicalUrl
    });
  }

  /**
   * Create or update a link tag
   * @param {LinkDefinition} tag
   */
  private updateTag(tag: LinkDefinition): void {
      const selector = this.getLinkAttributeSelector(tag);
      const linkElement = <HTMLLinkElement> this.document.head.querySelector(selector)
          || this.document.head.appendChild(this.document.createElement('link'));

      if (linkElement) {
          Object.keys(tag).forEach((prop: string) => {
              linkElement[prop] = tag[prop];
          });
      }
  }

  /**
   * Parse tag to create a selector (based on if rel or href is passed), used to find element with matching selector
   * @param tag the attribute used to create the selector
   * @return {string} selector to use in querySelector
   */
  private getLinkAttributeSelector(tag: LinkDefinition): string {
      const attr: string = tag.rel ? 'rel' : 'href';
      return `link[${attr}="${tag[attr]}"]`;
  }
}

export declare type LinkDefinition = {
  href?: string;
  rel?: string;
} & {
  [prop: string]: string;
};